import { FC, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ModalComponentBaseProps, useModal } from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody } from '@ingka/modal';
import { useContentTracking } from '@/lib/hooks/tracking';
import { TFunction } from 'i18next';
import Accordion, { AccordionItem } from '@ingka/accordion';

type FaqItem = {
  title: string;
  description: string;
  key: string;
  link?: string;
};

export type FaqType = 'users' | 'admins';

interface FaqQuestionsProps extends ModalComponentBaseProps {
  faqType: FaqType;
}

const useFaqTracking = (faqType: FaqType) => {
  const trackContent = useContentTracking();

  useEffect(() => {
    trackContent(`${faqType}-faq`, { type: 'accordion' });
  }, []);
};

const prepareFaq = (t: TFunction, faqType: FaqType): FaqItem[] => {
  const items = t(`page.faq.${faqType}.questions`, { returnObjects: true });
  return Object.entries(items)
    .map(([key]) => ({
      key,
      title: `page.faq.${faqType}.questions.${key}.title`,
      description: `page.faq.${faqType}.questions.${key}.description`,
      link: t(`page.faq.${faqType}.questions.${key}.link`),
    }))
    .filter(
      ({ title, description }) =>
        title !== t(title) && description !== t(description)
    ); // Filter incomplete translations
};

const FaqQuestions: FC<FaqQuestionsProps> = ({
  handleDismiss,
  faqType,
  ...rest
}) => {
  // Hooks
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();

  // Data
  const items = prepareFaq(t, faqType);

  // Event handlers
  useFaqTracking(faqType);
  const goBack = async () => {
    await closeModal(0);
    const { default: FAQSider } = await import('../menu');

    await openModal({
      component: FAQSider,
      props: {},
    });
  };

  return (
    <Sheets
      {...rest}
      data-testid={`${faqType}FaqModal`}
      aria-label={t(`page.faq.${faqType}.description`)}
      footer={null}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t(`page.faq.${faqType}.title`)}
          backBtnClick={goBack}
        />
      }
    >
      <ModalBody>
        <Accordion size="large">
          {items.map(({ key, title, description, link }) => {
            return (
              <AccordionItem
                id={`accordion-item_${key}`}
                key={key}
                title={<p className="px-6">{t(title)}</p>}
              >
                <p className="text-sm leading-snug px-6">
                  <Trans
                    i18nKey={description}
                    values={{
                      link,
                    }}
                    components={{
                      a: (
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                    }}
                  />
                </p>
              </AccordionItem>
            );
          })}
        </Accordion>
      </ModalBody>
    </Sheets>
  );
};

export default FaqQuestions;
